import Vue from 'vue';
import { isSharedViewFilters, isWatchlistFilters } from '@/models/tender-views';
import { locationsService } from '@/services/locations.service';
import { preProjectsService } from '@/services/pre-projects.service';

// The API items may carry no top-level id — fall back to the first cost
// entry's preProjectId (the same key the table derives its row ids from).
const preProjectIdOf = (preProject) =>
  preProject.id ||
  (preProject.costsByYear && preProject.costsByYear[0] && preProject.costsByYear[0].preProjectId) ||
  null;

// Mirrors the backend search filters DTO. `query` terms match the title
// (case-insensitive contains, ANY term); year/cost windows match by overlap.
const initialFilters = () => ({
  query: [],
  emitters: [],
  // Location levels form ONE filter on the backend: a pre-project matches when
  // any of its locations is a listed city, or lies in a listed region or province.
  provinceIds: [],
  regionIds: [],
  cityIds: [],
  fromYear: null,
  toYear: null,
  // The exact years picked in the year chip. UI-level: the backend consumes
  // only the fromYear/toYear window derived from them (see toSearchBody).
  years: [],
  minCost: null,
  maxCost: null,
  // True = only the user's followed pre-projects (the "Saved" built-in view).
  followed: null,
  // Tri-state origin: true = manual (tenant-entered) only, false = imported
  // (Aloha) only, null = both.
  manual: null,
});

// Any meaningful value counts — including keys beyond the base model that a
// view's saved filters may carry (e.g. `states`). `false` counts too: the
// tri-state `manual` filter uses it for "imported only".
const hasActiveFilters = (filters) =>
  Object.values(filters || {}).some((value) => value != null && !(Array.isArray(value) && value.length === 0));

const initialState = () => ({
  preProjects: [],
  // Every year / emitter covered across the whole result set (from the search
  // response) — they back the filter chips' options.
  availableYears: [],
  availableEmitters: [],
  // Location lookups. `provinces` backs the province filter chip; `cities`
  // backs the custom pre-project form and is cached per region scope
  // (citiesRegionId = null means the unfiltered list); `allCities` backs the
  // cities filter chip and always holds the full list.
  provinces: [],
  regions: [],
  cities: [],
  citiesRegionId: null,
  allCities: [],
  pagination: {
    page: 1,
    totalCount: 0,
    totalPages: 0,
  },
  filters: initialFilters(),
  // Newest first by default.
  sortingField: 'createdAt',
  sortingOrder: 'desc',
  // Ids with a share/unshare request in flight. One request per row at a
  // time: a second click while the first is pending could dispatch the
  // opposite operation, and the replies could land out of order.
  sharePendingIds: [],
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
  // On phones the list grows page by page instead of showing one page at a time.
  continuous: false,
  loadingMore: false,
  loadMoreError: null,
});

const state = initialState();

const PAGE_SIZE = 25;
// The most the search returns at once.
const MAX_PAGE_SIZE = 100;

// The search requests aren't cancelled: a page that answers after a newer search started
// belongs to the old results and is dropped.
let searchId = 0;

const actions = {
  getPreProjects({ commit, state }, { page = 1, pageSize = PAGE_SIZE } = {}) {
    searchId += 1;
    commit('PENDING');
    const { sortingField, sortingOrder, filters } = state;
    return preProjectsService.getPreProjects({ page, pageSize, sortingField, sortingOrder, filters }).then(
      ({ preProjects, availableYears, availableEmitters, pagination }) => {
        commit('GET_PRE_PROJECTS_SUCCESS', { preProjects, pagination });
        // The year/emitter options (column toggle + filter chips) describe the
        // whole dataset, so only an unfiltered search refreshes them —
        // filtering (e.g. by year) must not shrink the choices.
        if (!hasActiveFilters(state.filters) || !state.availableYears.length) {
          commit('SET_AVAILABLE_YEARS', availableYears || []);
        }
        if (!hasActiveFilters(state.filters) || !state.availableEmitters.length) {
          commit('SET_AVAILABLE_EMITTERS', availableEmitters || []);
        }
        return preProjects;
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  // Adds the next page under the listed ones. Not `pending`: the list stays usable while it
  // loads, and a failure keeps what's listed.
  loadMorePreProjects({ commit, state }, page) {
    const search = searchId;
    commit('LOAD_MORE_PRE_PROJECTS');
    const { sortingField, sortingOrder, filters } = state;
    return preProjectsService.getPreProjects({ page, pageSize: PAGE_SIZE, sortingField, sortingOrder, filters }).then(
      ({ preProjects, pagination }) => {
        if (search === searchId) commit('APPEND_PRE_PROJECTS', { preProjects, pagination });
        else commit('LOAD_MORE_CANCELLED');
      },
      (error) => commit(search === searchId ? 'LOAD_MORE_ERROR' : 'LOAD_MORE_CANCELLED', error),
    );
  },
  // Reloads every page listed on a phone (after an edit, a delete or an undo), so the reader
  // keeps their place: as many rows as are listed, at most MAX_PAGE_SIZE per request.
  reloadListedPreProjects({ commit, state }) {
    searchId += 1;
    const search = searchId;
    commit('PENDING');
    const { sortingField, sortingOrder, filters } = state;
    const requests = Math.ceil((state.pagination.page * PAGE_SIZE) / MAX_PAGE_SIZE);
    const fetchPage = (page) =>
      preProjectsService.getPreProjects({ page, pageSize: MAX_PAGE_SIZE, sortingField, sortingOrder, filters });
    let totalCount = 0;
    return Array.from({ length: requests }, (_, index) => index + 1)
      .reduce(
        (listed, page) =>
          listed.then((preProjects) =>
            fetchPage(page).then((response) => {
              totalCount = response.pagination.totalCount;
              return [...preProjects, ...response.preProjects];
            }),
          ),
        Promise.resolve([]),
      )
      .then(
        (preProjects) => {
          if (search !== searchId) return;
          const pagination = {
            page: Math.max(1, Math.ceil(preProjects.length / PAGE_SIZE)),
            totalCount,
            totalPages: Math.ceil(totalCount / PAGE_SIZE),
          };
          commit('GET_PRE_PROJECTS_SUCCESS', { preProjects, pagination });
        },
        (error) => {
          if (search !== searchId) return;
          commit('ERROR', error);
          throw error;
        },
      );
  },
  setContinuous({ commit }, continuous) {
    commit('SET_CONTINUOUS', continuous);
  },
  setSortingField({ commit }, sortingField) {
    commit('SET_SORTING_FIELD', sortingField);
  },
  setSortingOrder({ commit }, sortingOrder) {
    commit('SET_SORTING_ORDER', sortingOrder);
  },
  // Location lookups. Provinces and regions are fetched once; cities re-fetch
  // whenever the requested region scope changes.
  getProvinces({ commit, state }) {
    if (state.provinces.length) return Promise.resolve(state.provinces);
    return locationsService.getProvinces().then((provinces) => {
      commit('SET_PROVINCES', provinces || []);
      return provinces;
    });
  },
  getRegions({ commit, state }) {
    if (state.regions.length) return Promise.resolve(state.regions);
    return locationsService.getRegions().then((regions) => {
      commit('SET_REGIONS', regions || []);
      return regions;
    });
  },
  getCities({ commit, state }, { regionId = null } = {}) {
    if (state.cities.length && state.citiesRegionId === regionId) return Promise.resolve(state.cities);
    return locationsService.getCities({ regionId }).then((cities) => {
      commit('SET_CITIES', { cities: cities || [], regionId });
      return cities;
    });
  },
  // Full city list for the cities filter chip (fetched once, cached).
  getAllCities({ commit, state }) {
    if (state.allCities.length) return Promise.resolve(state.allCities);
    return locationsService.getCities().then((cities) => {
      commit('SET_ALL_CITIES', cities || []);
      return cities;
    });
  },

  // Custom (tenant-entered) pre-projects — the caller refreshes the list on success.
  createPreProject(context, preProject) {
    return preProjectsService.createPreProject(preProject);
  },
  updatePreProject(context, { preProjectId, preProject }) {
    return preProjectsService.updatePreProject(preProjectId, preProject);
  },
  deletePreProject(context, preProjectId) {
    return preProjectsService.deletePreProject(preProjectId);
  },

  // Follow/unfollow a pre-project (the "Sauvegardés" list) via
  // PUT /pre-projects/{id}/follow { enabled }. Optimistic — the row flips
  // immediately and rolls back if the call fails.
  follow({ commit }, preProjectId) {
    commit('SET_FOLLOWED', { preProjectId, followed: true });
    return preProjectsService.setFollowed(preProjectId, true).catch((error) => {
      commit('SET_FOLLOWED', { preProjectId, followed: false });
      throw error;
    });
  },
  unfollow({ commit, state }, preProjectId) {
    commit('SET_FOLLOWED', { preProjectId, followed: false });
    return preProjectsService.setFollowed(preProjectId, false).then(
      (result) => {
        // On the watchlist view an unfollowed pre-project no longer belongs to
        // the result set — drop the row right away instead of waiting for a refresh.
        if (isWatchlistFilters(state.filters)) commit('REMOVE_PRE_PROJECT', preProjectId);
        return result;
      },
      (error) => {
        commit('SET_FOLLOWED', { preProjectId, followed: true });
        throw error;
      },
    );
  },
  // Share / unshare with the whole tenant, optimistic like follow: the row flips
  // at once and flips back if the API refuses (trial tenant, not visible).
  // The row is marked pending until the request settles (see sharePendingIds).
  share({ commit }, preProjectId) {
    commit('SET_SHARED', { preProjectId, shared: true });
    commit('SET_SHARE_PENDING', { preProjectId, pending: true });
    return preProjectsService
      .setShared(preProjectId, true)
      .then(
        (result) => {
          commit('SET_SHARED', {
            preProjectId,
            shared: true,
            sharedDate: result.sharedDate,
            sharedBy: result.sharedBy,
          });
          return result;
        },
        (error) => {
          commit('SET_SHARED', { preProjectId, shared: false });
          throw error;
        },
      )
      .finally(() => commit('SET_SHARE_PENDING', { preProjectId, pending: false }));
  },
  unshare({ commit, state }, preProjectId) {
    // The optimistic flip clears the share stamp too — keep it to restore on refusal.
    const before = state.preProjects.find((item) => preProjectIdOf(item) === preProjectId) || {};
    const stamp = { sharedDate: before.sharedDate, sharedBy: before.sharedBy };
    commit('SET_SHARED', { preProjectId, shared: false });
    commit('SET_SHARE_PENDING', { preProjectId, pending: true });
    return preProjectsService
      .setShared(preProjectId, false)
      .then(
        (result) => {
          // On the shared view an unshared pre-project no longer belongs to the
          // result set — drop the row right away instead of waiting for a refresh.
          if (isSharedViewFilters(state.filters)) commit('REMOVE_PRE_PROJECT', preProjectId);
          return result;
        },
        (error) => {
          commit('SET_SHARED', { preProjectId, shared: true, ...stamp });
          throw error;
        },
      )
      .finally(() => commit('SET_SHARE_PENDING', { preProjectId, pending: false }));
  },
  // Merge a partial update into the current filters (only the given keys change).
  setFilters({ commit }, partialFilters) {
    commit('SET_FILTERS', partialFilters);
  },
  // Full replace (not a merge): keys outside the base model — e.g. extra fields
  // carried by a view's saved filters — must not survive a clear.
  clearFilters({ commit }) {
    commit('RESET_FILTERS');
  },
};

const mutations = {
  PENDING(state) {
    state.status = { error: null, pending: true, resolved: false, rejected: false };
    state.loadingMore = false;
    state.loadMoreError = null;
  },
  SET_CONTINUOUS(state, continuous) {
    state.continuous = continuous;
  },
  LOAD_MORE_PRE_PROJECTS(state) {
    state.loadingMore = true;
    state.loadMoreError = null;
  },
  APPEND_PRE_PROJECTS(state, { preProjects, pagination }) {
    // One added since the previous page shifts the pages: its last row comes back first.
    const listed = new Set(state.preProjects.map(preProjectIdOf));
    state.preProjects = [...state.preProjects, ...preProjects.filter((item) => !listed.has(preProjectIdOf(item)))];
    state.pagination = pagination;
    state.loadingMore = false;
  },
  LOAD_MORE_ERROR(state, error) {
    state.loadingMore = false;
    state.loadMoreError = error;
  },
  LOAD_MORE_CANCELLED(state) {
    state.loadingMore = false;
  },
  GET_PRE_PROJECTS_SUCCESS(state, { preProjects, pagination }) {
    state.preProjects = preProjects;
    state.pagination = pagination;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  SET_AVAILABLE_YEARS(state, availableYears) {
    state.availableYears = availableYears;
  },
  SET_AVAILABLE_EMITTERS(state, availableEmitters) {
    state.availableEmitters = availableEmitters;
  },
  SET_PROVINCES(state, provinces) {
    state.provinces = provinces;
  },
  SET_REGIONS(state, regions) {
    state.regions = regions;
  },
  SET_CITIES(state, { cities, regionId }) {
    state.cities = cities;
    state.citiesRegionId = regionId;
  },
  SET_ALL_CITIES(state, cities) {
    state.allCities = cities;
  },
  ERROR(state, error) {
    state.status = { ...state.status, pending: false, rejected: true, error };
  },
  SET_FOLLOWED(state, { preProjectId, followed }) {
    const preProject = state.preProjects.find((item) => preProjectIdOf(item) === preProjectId);
    if (preProject) Vue.set(preProject, 'followed', followed);
  },
  SET_SHARED(state, { preProjectId, shared, sharedDate = null, sharedBy = null }) {
    const preProject = state.preProjects.find((item) => preProjectIdOf(item) === preProjectId);
    if (!preProject) return;
    Vue.set(preProject, 'shared', shared);
    Vue.set(preProject, 'sharedDate', shared ? sharedDate : null);
    Vue.set(preProject, 'sharedBy', shared ? sharedBy : null);
  },
  SET_SHARE_PENDING(state, { preProjectId, pending }) {
    const others = state.sharePendingIds.filter((id) => id !== preProjectId);
    state.sharePendingIds = pending ? [...others, preProjectId] : others;
  },
  REMOVE_PRE_PROJECT(state, preProjectId) {
    const index = state.preProjects.findIndex((item) => preProjectIdOf(item) === preProjectId);
    if (index === -1) return;
    state.preProjects.splice(index, 1);
    state.pagination = { ...state.pagination, totalCount: Math.max(0, state.pagination.totalCount - 1) };
  },
  SET_FILTERS(state, partialFilters) {
    state.filters = { ...state.filters, ...partialFilters };
  },
  RESET_FILTERS(state) {
    state.filters = initialFilters();
  },
  SET_SORTING_FIELD(state, sortingField) {
    state.sortingField = sortingField;
  },
  SET_SORTING_ORDER(state, sortingOrder) {
    state.sortingOrder = sortingOrder;
  },
  RESET(state) {
    Object.assign(state, initialState());
  },
};

const getters = {
  preProjects: (state) => state.preProjects,
  availableYears: (state) => [...state.availableYears].sort((a, b) => a - b),
  availableEmitters: (state) => [...state.availableEmitters].sort((a, b) => a.localeCompare(b)),
  provinces: (state) => state.provinces,
  regions: (state) => state.regions,
  cities: (state) => state.cities,
  citiesRegionId: (state) => state.citiesRegionId,
  allCities: (state) => state.allCities,
  filters: (state) => state.filters,
  page: (state) => state.pagination.page,
  totalCount: (state) => state.pagination.totalCount,
  totalPages: (state) => state.pagination.totalPages,
  pending: (state) => state.status.pending,
  continuous: (state) => state.continuous,
  loadingMore: (state) => state.loadingMore,
  loadMoreError: (state) => state.loadMoreError,
  sortingField: (state) => state.sortingField,
  sortingOrder: (state) => state.sortingOrder,
  // Whether a share/unshare request for the given pre-project is in flight.
  isSharePending: (state) => (preProjectId) => state.sharePendingIds.includes(preProjectId),
  resolved: (state) => state.status.resolved,
  rejected: (state) => state.status.rejected,
  error: (state) => state.status.error,
};

export const preProjects = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
